import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { EditIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { StandardizedApiError } from '@/services/client/types';
import { LocationStatusPayload } from '@/services/locations/locations.mutations';
import { LOCATION_STATUS, LocationData } from '@/services/locations/locations.types';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from 'ui/button';
import ManageLocationForm from '../ManageLocationForm';

export type ModalSize = 'sm' | 'md' | 'lg';
export type ModalVariant = 'default' | 'destructive' | 'warning';
export function getColumns(
  onOpen: <T extends string>(config: {
    title: T;
    modalTitle: string | null;
    okText: React.ReactNode;
    component: React.ReactNode;
    cancelText?: string | React.ReactNode;
    size?: ModalSize;
    onCancel?: () => void;
  }) => void,
  mutateAsyncLocationStatus: UseMutateAsyncFunction<
    {
      message: string;
      data: LocationData;
    },
    StandardizedApiError,
    LocationStatusPayload,
    unknown
  >,
  openConfirmation: (data: {
    title: string;
    description: string;
    cancelLabel: string;
    actionLabel: string;
    content?: React.ReactNode;
    size?: ModalSize;
    variant?: ModalVariant;
    onAction: () => void | Promise<void>;
    onCancel: () => void;
  }) => void,
  t: (key: string) => string,
): ColumnDef<LocationData>[] {
  return [
    {
      accessorKey: 'id',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="font-medium truncate max-w-[10rem]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'locationTitle',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.locationTitle')} />
      ),
      cell: ({ row }) => <div className="font-medium truncate max-w-[20rem]">{row.getValue('locationTitle')}</div>,
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: 'locationSubtitle',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.locationSubTitle')} />
      ),
      cell: ({ row }) => <div>{row.getValue('locationSubtitle')}</div>,

      enableHiding: false,
    },
    {
      accessorKey: 'totalClubs',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalNumberOfClubs')} />
      ),
      cell: ({ row }) => <div>{row.getValue('totalClubs')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) => (
        <div>
          {row.original.status === LOCATION_STATUS.ACTIVE ? (
            <div className="text-success text-caption font-medium">{t('common.page.table.active')}</div>
          ) : (
            <div className="text-destructive-text text-caption font-medium">{t('common.page.table.blocked')}</div>
          )}
        </div>
      ),

      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      id: 'action',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      enableHiding: false,
      enableSorting: false,
      accessorKey: 'action',
      cell: ({ row }) => (
        <div className="flex gap-4 items-center">
          <Can rule={{ allOf: [PERMS.location.detailView, PERMS.location.update] }}>
            {row.original.status === LOCATION_STATUS.ACTIVE && (
              <EditIcon
                className="text-foreground-subtle size-4 hover:cursor-pointer"
                onClick={() => {
                  const locationId = row.original.locationId;
                  onOpen({
                    modalTitle: 'configurationManagement.location.page.editModal.manageLocation',
                    title: 'Manage Location',
                    okText: t('configurationManagement.location.page.editModal.confirm'),
                    size: 'lg',
                    cancelText: t('common.page.modal.no'),
                    component: <ManageLocationForm locationId={locationId} />,
                  });
                }}
              />
            )}
          </Can>
          <Can rule={{ allOf: [PERMS.location.statusUpdate] }}>
            <Button
              variant={'ghost'}
              className="p-0"
              onClick={() => {
                const isActive = row.getValue('status') === LOCATION_STATUS.ACTIVE;
                openConfirmation({
                  title: t('common.page.modal.confirm'),
                  description: isActive
                    ? t('configurationManagement.location.page.deleteModel.blockLocation')
                    : t('configurationManagement.location.page.deleteModel.unblockLocation'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.submit'),
                  size: 'lg',
                  onAction: async () => {
                    await mutateAsyncLocationStatus({
                      locationId: row.original.locationId,
                      locationStatus:
                        row.original.status === LOCATION_STATUS.ACTIVE
                          ? LOCATION_STATUS.BLOCKED
                          : LOCATION_STATUS.ACTIVE,
                    });
                  },
                  onCancel: () => {},
                  variant: 'warning',
                });
              }}
            >
              {row.getValue('status') === LOCATION_STATUS.ACTIVE ? (
                <LockCloseIcon className="text-destructive-text size-4 " />
              ) : (
                <LockOpenIcon className="text-link size-4 " />
              )}
            </Button>
          </Can>
        </div>
      ),
      size: 40,
    },
  ];
}
